<template>
	<card v-if="activityUser && props.activity.owner" :id="'activity-' + props.activity.id" class="activity" :flat="props.flat" :outlined="props.outlined" :xs-tile="props.xsTile" :no-spacing="props.noSpacing">
		<div class="flex justify-between px-16 lg:px-24 pt-20 mb-8">
			<div class="flex items-center overflow-hidden pb-8">
				<nuxt-link :to="$spoferanUrl(`${activityUser.model_name === 'user' ? 'athlete' : activityUser.model_name}s/${activityUser._key}`)" class="relative">
					<avatar :size="activityUser.model_type === ModelType.Event ? 54 : 36" :model="activityUser"/>
					<avatar v-if="activityUser.club" :model="activityUser.club" :size="16" class="absolute bottom-0 right-0 mr--4 mb--4"/>
				</nuxt-link>
				<div class="flex ml-12 sm:ml-16 flex-column overflow-hidden">
					<div class="flex items-center overflow-hidden">
						<nuxt-link :to="$spoferanUrl(`${activityUser.model_name === 'user' ? 'athlete' : activityUser.model_name}s/${activityUser._key}`)"
									  class="font-bold leading-normal text-heading-color hover:text-primary text-sm truncate">
							{{ activityUser.display_name }}
						</nuxt-link>

						<tooltip v-if="props.activity.privacy === PrivacyType.Public" bottom :text="$t('text.activity.privacy.public')" with-touch>
							<Icon size="14" color="gray-300" class="ml-5" :icon="icons.privacy.public"/>
						</tooltip>
						<tooltip v-else-if="props.activity.privacy === PrivacyType.Community && activityUser.model_type === ModelType.Event" bottom with-touch
								 :text="$t('text.activity.privacy.event_community')">
							<Icon size="14" color="gray-300" class="ml-5" :icon="icons.privacy.community"/>
						</tooltip>
						<tooltip v-else-if="props.activity.privacy === PrivacyType.Community" bottom with-touch
								 :text="$t('text.activity.privacy.community')">
							<Icon size="14" color="gray-300" class="ml-5" :icon="icons.privacy.community"/>
						</tooltip>
						<tooltip v-else-if="props.activity.privacy === PrivacyType.Private" bottom with-touch
								 :text="$t('text.activity.privacy.private')">
							<Icon size="14" color="gray-300" class="ml-5" :icon="icons.privacy.private"/>
						</tooltip>
						<tooltip v-else-if="props.activity.privacy === PrivacyType.Participants" bottom with-touch
								 :text="$t('text.activity.privacy.participants')">
							<Icon size="14" color="gray-300" class="ml-5" :icon="icons.privacy.participants"/>
						</tooltip>

						<client-only>
							<tooltip
								v-if="props.activity.in_environment"
								with-touch
								bottom :text="$t('text.activity.privacy.environment')">
								<Icon size="14" color="gray-300" class="ml-5" :icon="icons.privacy.environment"/>
							</tooltip>
						</client-only>
					</div>

					<span
						v-if="props.activity.type === ActivityType.NewsPublished && props.feedOwner && props.activity.owner && (props.activity.owner.model_type !== props.feedOwner.model_type || props.activity.owner.id !== props.feedOwner.id)"
						class="text-xs leading-normal mt-2 flex overflow-hidden">
                        <span class="whitespace-no-wrap mr-1">{{ $t('conjunction.reports_about') }}&nbsp;</span>
                        <nuxt-link :to="$spoferanUrl(`${props.feedOwner.model_name === 'user' ? 'athlete' : props.feedOwner.model_name}s/${props.feedOwner._key}`)" :style="{color: props.feedOwner.color}" class="truncate">{{ props.feedOwner.display_name }}</nuxt-link>
                    </span>
					<span
						v-else-if="props.activity.creator && (props.activity.creator.model_type !== props.activity.owner.model_type || props.activity.creator.id !== props.activity.owner.id) && (!props.feedOwner || !props.activity.owner || props.activity.owner.model_type !== props.feedOwner.model_type || props.activity.owner.id !== props.feedOwner.id)"
						class="text-xs leading-normal mt-2 flex overflow-hidden">
                        <span>{{ $t('conjunction.at_page') }}&nbsp;</span>
                        <nuxt-link :to="$spoferanUrl(`${props.activity.owner.model_name === 'user' ? 'athlete' : props.activity.owner.model_name}s/${props.activity.owner._key}`)"
									  :style="{color: props.activity.owner.color}" class="truncate">{{ props.activity.owner.display_name }}</nuxt-link>
                    </span>
					<span v-else-if="activityUser.model_type === ModelType.Club" class="text-xs mt-2 leading-normal">
                        {{ activityUser.parent_club_id ? $t('label.department') : activityUser.presentation_type_name }}</span>
					<span v-else-if="activityUser.model_type === ModelType.User" class="text-xs mt-2 leading-normal">
                        {{ activityUser.presentation_type_name }}</span>
					<span v-else class="text-xs mt-2 leading-normal">
                        {{ activityUser.type ? activityUser.type.title : $t('label.event') }}</span>
				</div>
			</div>
			<div v-if="!props.preview && (editable || deletable)" class="ml-8">
				<dropdown left>
					<Icon :icon="icons.more" small color="gray-400" class="cursor-pointer hover:text-primary"/>
					<template #menu>
						<nav-list>
							<nav-list-item v-if="editable" @click="emit('edit')">{{ $t('action.edit') }}</nav-list-item>
							<form-nav-list-item v-if="deletable && props.feedOwner"
												:action="`/v1/${props.feedOwner.model_name}s/${props.feedOwner.id}/activities/${activity.id}/link`"
												method="DELETE"
												notification="destroyed"
												color="warning"
												@success="emit('destroyed')"
												:confirm="{title: $t('confirm.activity.hide.title'), message: $t('confirm.activity.hide.message')}">
								{{ $t('action.hide') }}
							</form-nav-list-item>
							<form-nav-list-item v-if="deletable"
												:action="`/v1/${props.activity.owner.model_name}s/${props.activity.owner.id}/activities/${props.activity.id}`"
												method="DELETE"
												notification="destroyed"
												color="error"
												@success="emit('destroyed')"
												:confirm="{title: $t('confirm.activity.destroy.title'), message: $t('confirm.activity.destroy.message')}">
								{{ $t('action.destroy') }}
							</form-nav-list-item>
						</nav-list>
					</template>
				</dropdown>
			</div>
		</div>

		<card-text class="text-sub py-0" v-if="displayText || props.activity.linked">
			<div v-if="displayText" class="mb-8">
				<user-content class="whitespace-pre-line" :text="activityText"/>

				<div v-if="!props.preview && stripedText.length > maxContentLength" class="text-center text-sm mt-5 mb-0">
					<btn text :color="props.color ?? $config.public.colors.primary" v-if="!showMore" @click.prevent="showMore = true">{{ $t('action.show_more') }}</btn>
					<btn text :color="props.color ?? $config.public.colors.primary" @click.prevent="showMore = false" v-else>{{ $t('action.show_less') }}</btn>
				</div>
			</div>

			<div v-if="!props.preview && props.activity.linked && !mediaSources.length" class="py-8">
				<div v-if="props.activity.linked.model_type === ModelType.Participation" class="flex flex-column sm:flex-row rounded border"
					 :style="{borderLeft: `2px solid ${props.activity.linked.event.color ?? $config.public.colors.primary} !important`}">
					<image-modal v-if="props.activity.linked.image" :src="{path: props.activity.linked.image, size: {width: '100vw', sm: {height: 400}, xl: {height: 600}},preview: {size: {ratio: 1, width: '100vw', sm: {width: 180}}}}" background-color class="sm:min-w-180"/>

					<lazy-event-participations-preview :event="props.activity.linked.event" :participations="[props.activity.linked]" without-interaction without-image class="w-full overflow-hidden"/>
				</div>

				<lazy-profile-preview v-else-if="props.activity.linked.model_type === ModelType.Participation" outlined no-spacing :model="props.activity.linked.event"/>
				<lazy-article-card v-else-if="props.activity.linked.model_type === ModelType.Article" outlined no-spacing :article="props.activity.linked"/>
				<lazy-profile-preview v-else outlined no-spacing :model="props.activity.linked"/>
			</div>
		</card-text>

		<lazy-watchbetter-tip-of-the-day-embed v-if="props.activity.type === ActivityType.TipOfTheDay" category="sport_fitness" class="mb-4"/>

		<image-modal v-if="!preview && mediaSources.length" :src="mediaSources" :show-pagination="mediaSources.length > 1" rounded item-class="px-16 lg:px-24" class="py-8"/>

		<lazy v-if="!props.preview && props.activity.url" :options="{threshold: .5}">
			<link-preview :link="props.activity.url" :class="{'mt-8': !mediaSources.length}"/>
		</lazy>

		<div v-if="!props.preview" class="flex justify-between items-center px-16 lg:px-24">
			<time class="text-xs text-subtext-color">
				{{ $date(props.activity.published_at).calendar($date(props.activity.published_at).year() === $date().year() ? 'DD. MMM' : 'DD. MMM YYYY') }}
			</time>

			<btn-group class="justify-end" text>
				<like-form :action="props.readonly ? undefined : `/v1/${props.activity.owner.model_name}s/${props.activity.owner.id}/activities/${props.activity.id}/likes`"
						   :count="props.activity.likes_count"
						   :liked="props.activity.likes?.some(like => like.id === $auth.id)"
						   :likes="props.activity.likes ?? []"
						   :readonly="props.readonly"
						   :color="props.color ?? $config.public.colors.primary"
						   @show="$emit('show-likes')"
						   ref="likeForm"/>

				<tooltip bottom :text="$t('label.comments')">
					<btn @click="$emit('show-comments')" :disabled="props.readonly" text large>
						<Icon small force-size :icon="icons.comment"/>
						<span>{{ props.activity.comments_count }}</span>
					</btn>
				</tooltip>
			</btn-group>
		</div>
	</card>
</template>

<script setup lang="ts">
import {mdiAccountGroup, mdiAccountMultiple, mdiAccountStar, mdiCommentMultiple, mdiEarth, mdiLock, mdiMapMarkerOutline, mdiShieldAccount} from '@mdi/js'
import type {Activity, MainModelPreview} from "@spoferan/spoferan-ts-core";
import {ActivityType, ModelType, PrivacyType, stripHtml, transformUrlsToLinks, truncate,} from "@spoferan/spoferan-ts-core";
import {moreIcon} from "../../../config";
import {useNuxtApp} from '#imports';
import {computed, ref} from "vue";

const {$auth} = useNuxtApp();
const icons = {
	comment: mdiCommentMultiple,
	more: moreIcon,
	privacy: {
		public: mdiEarth,
		environment: mdiMapMarkerOutline,
		community: mdiAccountGroup,
		followers: mdiAccountStar,
		members: mdiShieldAccount,
		private: mdiLock,
		participants: mdiAccountMultiple,
	}
}

const props = defineProps<{
	activity: Activity,

	// The model of which feed the activity is displayed
	feedOwner?: MainModelPreview | null,

	// Whether to prevent any shadow
	flat?: boolean,

	// Whether to display a border style
	outlined?: boolean,

	xsTile?: boolean,
	smTile?: boolean,

	// Whether to only show a short preview snippet of the activity
	preview?: boolean,

	noSpacing?: boolean,

	// Whether to disable edits
	withoutEdit?: boolean,

	// Whether to disable edits, likes and comments
	readonly?: boolean,

	color?: string,
}>();
const emit = defineEmits(['show-likes', 'show-comments', 'edit', 'destroyed']);

// States if the full activity content is currently shown
const showMore = ref(false);

// The max length of the activity text before the 'show more' button will be shown
const maxContentLength = props.preview ? 250 : 500;

const stripedText = computed(() => {
	return stripHtml(props.activity.text);
});

const displayText = computed(() => {
	return props.activity.text ? transformUrlsToLinks(props.activity.text) : null;
});

// The user to show as the creator of the activity
const activityUser = computed(() => {
	return props.activity.creator ?? props.activity.owner
});

// The creator's activity text to show on the activity content
const activityText = computed(() => {
	if (showMore.value) {
		return displayText.value
	}

	return truncate(displayText.value, maxContentLength)
});

const mediaSources = computed(() => {
	if (!props.activity.media || !props.activity.media.length) {
		return [];
	}

	return props.activity.media.map(media => {
		const isVertical = media.source.ratio < 1;
		if (isVertical) {
			return {
				path: media.source,
				size: {width: '100vw', ratio: media.source.ratio, sm: {height: 400}, xl: {height: 600}},
				preview: {path: media.source_thumbnail, size: {ratio: media.source_thumbnail.ratio, height: 400, xl: {height: 480}}}
			}
		}

		return {
			path: media.source,
			size: {width: '100vw', ratio: media.source.ratio, sm: {height: 400}, xl: {height: 600}},
			preview: {path: media.source_thumbnail, size: {ratio: media.source_thumbnail.ratio, height: 400, sm: {width: 712}}}
		}
	});
});

const editable = computed(() => {
	if (props.withoutEdit || props.readonly || !props.activity.creator || !$auth.user) {
		return false;
	}

	return [$auth.user, ...$auth.user.actAs].find(actAs => actAs.model_type === props.activity.creator.model_type && actAs.id === props.activity.creator.id);
});

const deletable = computed(() => {
	if (editable.value) {
		return true;
	}

	if (props.withoutEdit || props.readonly || !props.activity.owner || !$auth.user) {
		return false;
	}

	return [$auth.user, ...$auth.user.actAs].find(actAs => actAs.model_type === props.activity.owner.model_type && actAs.id === props.activity.owner.id);
});

</script>